import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { DraftDiff } from '../components/DraftDiff';
import { PageHeader } from '../components/PageHeader';
import { unwrap, useApi } from '../lib/api';
import { draftStore, StaleBaseError } from '../lib/draft';
import { collections, useEnsureDraft } from '../lib/contentConfig';
import { toContentBundle, validateBundle } from '../lib/validate';
import { ReachabilityReport } from './TechTreePage';

const btn =
  'rounded border-2 border-ink px-3 py-1.5 text-sm font-semibold hover:bg-caption/50 disabled:opacity-50';

/** Validate the draft on the server, publish it as the next version, or roll back to an older one. */
export function PublishPage() {
  const { client } = useApi();
  const qc = useQueryClient();
  const { draft, loading } = useEnsureDraft();
  const [notes, setNotes] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [conflict, setConflict] = useState<StaleBaseError | null>(null);
  const diffRef = useRef<HTMLElement>(null);

  const versions = useQuery({
    queryKey: ['admin-content-versions'],
    queryFn: async () => unwrap(await client.GET('/admin/content')),
  });

  const validate = useMutation({
    mutationFn: async () =>
      unwrap(
        await client.POST('/admin/content/validate', { body: toContentBundle(draft!.bundle) }),
      ),
  });

  // Publish sends the draft's baseVersion, so the API refuses (409 stale_base) if someone published since the draft
  // started. "Publish anyway" resends without it (last writer wins, the old behaviour).
  const publish = useMutation({
    mutationFn: async ({ anyway }: { anyway: boolean }) => {
      const result = await client.POST('/admin/content/publish', {
        body: {
          bundle: toContentBundle(draft!.bundle),
          notes,
          ...(anyway ? {} : { baseVersion: draft!.baseVersion }),
        },
      });
      if (result.response.status === 409) {
        const p = (result.error ?? {}) as {
          code?: string;
          baseVersion?: number;
          currentVersion?: number;
        };
        if (p.code === 'stale_base')
          throw new StaleBaseError(p.baseVersion ?? draft!.baseVersion, p.currentVersion ?? NaN);
      }
      return unwrap(result);
    },
    onMutate: () => {
      setConflict(null);
      setMessage(null);
    },
    onSuccess: (v) => {
      setMessage(`Published version ${v.version}. Players get it on their next check.`);
      draftStore.set(null);
      setNotes('');
      void qc.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof StaleBaseError) {
        setConflict(e);
        // Refresh the version list so the diff below compares against the version that's live now.
        void qc.invalidateQueries({ queryKey: ['admin-content-versions'] });
      } else setMessage(String(e));
    },
  });

  const rollback = useMutation({
    mutationFn: async (version: number) =>
      unwrap(
        await client.POST('/admin/content/{version}/rollback', {
          params: { path: { version } },
          body: { notes: null },
        }),
      ),
    onSuccess: (v) => {
      setMessage(`Republished as version ${v.version}.`);
      draftStore.set(null);
      void qc.invalidateQueries();
    },
  });

  const latest = versions.data?.[0]?.version;
  const published = useQuery({
    queryKey: ['admin-content', latest],
    queryFn: async () =>
      unwrap(
        await client.GET('/admin/content/{version}', {
          params: { path: { version: latest ?? 0 } },
        }),
      ),
    enabled: latest !== undefined,
  });
  const issues = useMemo(() => (draft ? validateBundle(draft.bundle) : []), [draft]);
  const clientErrors = issues.filter((i) => i.severity === 'error');

  if (loading || !draft) return <p>Loading content&hellip;</p>;
  const stale = latest !== undefined && draft.baseVersion < latest;

  return (
    <>
      <PageHeader
        title="Publish"
        description="Check the draft with the server's rules, then publish it as a new immutable version."
      />

      <section className="mb-6 rounded border-2 border-ink bg-white p-4">
        <h2 className="font-semibold">Draft</h2>
        <p className="text-sm text-bruise">
          Based on version {draft.baseVersion}.{' '}
          {Object.entries(collections)
            .map(
              ([key, c]) =>
                // `?? []`: a draft from an older bundle may not have a newer collection (survivorTraits).
                `${((draft.bundle[key as keyof typeof collections] as unknown[] | undefined) ?? []).length} ${c.title.toLowerCase()}`,
            )
            .join(' · ')}
        </p>
        {stale && (
          <p className="mt-2 rounded bg-caption/40 p-2 text-sm">
            Version {latest} was published after this draft started. Publishing asks before it
            replaces it; discard the draft to start from the latest.
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={btn}
            onClick={() => validate.mutate()}
            disabled={validate.isPending}
          >
            Validate
          </button>
          <input
            aria-label="Notes"
            placeholder="What changed?"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="min-w-64 flex-1 rounded border-2 border-ink px-2 py-1 text-sm"
          />
          <button
            type="button"
            className={`${btn} bg-blam text-white`}
            onClick={() => publish.mutate({ anyway: false })}
            disabled={publish.isPending || !notes}
          >
            Publish
          </button>
          <button
            type="button"
            className={btn}
            onClick={() => confirm('Discard every change in the draft?') && draftStore.set(null)}
          >
            Discard draft
          </button>
        </div>
        <div className="mt-3 text-sm">
          {clientErrors.length > 0 ? (
            <>
              <p className="font-semibold text-blam">
                The editor found {clientErrors.length} error(s) the server will reject. Fix them in
                the editors (rows marked ●), then validate.
              </p>
              <ul className="list-disc pl-5 text-blam">
                {clientErrors.slice(0, 20).map((e, n) => (
                  <li key={n}>{e.message}</li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-bruise">
              Editor checks passed
              {issues.length > 0 ? ` (${issues.length} warning(s) in the editors)` : ''}. The server
              has the final say.
            </p>
          )}
        </div>
        {validate.data && (
          <div className="mt-3 text-sm">
            {validate.data.valid ? (
              <p className="text-benday">Valid. Ready to publish.</p>
            ) : (
              <ul className="list-disc pl-5 text-blam">
                {validate.data.errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}
            {/* ContentValidator.Warnings: publishing still works (e.g. an unlimited trigger). */}
            {(validate.data.warnings ?? []).length > 0 && (
              <ul className="mt-1 list-disc pl-5 text-bruise italic">
                {validate.data.warnings!.map((w) => (
                  <li key={w}>Warning: {w}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        {conflict && (
          <div role="alert" className="mt-3 rounded border-2 border-blam bg-caption/40 p-3 text-sm">
            <p className="font-semibold text-blam">{conflict.message}</p>
            <p className="mt-1">
              Publishing this draft as it is would undo what was published after version{' '}
              {conflict.baseVersion}. Review the changes below, or discard the draft and start again
              from the latest.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                className={btn}
                onClick={() => diffRef.current?.scrollIntoView?.({ behavior: 'smooth' })}
              >
                Review the diff
              </button>
              <button
                type="button"
                className={`${btn} bg-blam text-white`}
                onClick={() => publish.mutate({ anyway: true })}
                disabled={publish.isPending || !notes}
              >
                Publish anyway
              </button>
            </div>
          </div>
        )}
        {message && <p className="mt-3 text-sm font-semibold">{message}</p>}
      </section>

      <ReachabilityReport bundle={draft.bundle} />

      <section ref={diffRef} className="mb-6 rounded border-2 border-ink bg-white p-4">
        <h2 className="font-semibold">
          Changes against the published version{latest !== undefined ? ` (${latest})` : ''}
        </h2>
        {published.data ? (
          <DraftDiff published={published.data} draft={draft.bundle} />
        ) : published.error ? (
          <p className="text-sm text-blam">Could not load the published version.</p>
        ) : (
          <p className="text-sm text-bruise">Loading&hellip;</p>
        )}
      </section>

      <section>
        <h2 className="mb-2 font-semibold">Published versions</h2>
        <table className="w-full border-2 border-ink bg-white text-sm">
          <thead className="bg-ink text-newsprint">
            <tr>
              <th className="px-2 py-1 text-left">Version</th>
              <th className="px-2 py-1 text-left">Published</th>
              <th className="px-2 py-1 text-left">By</th>
              <th className="px-2 py-1 text-left">Notes</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {versions.data?.map((v, i) => (
              <tr key={v.version} className="border-t border-newsprint">
                <td className="px-2 py-1 font-mono">{v.version}</td>
                <td className="px-2 py-1">{new Date(v.publishedAt).toLocaleString()}</td>
                <td className="px-2 py-1">{v.publishedBy ?? 'system'}</td>
                <td className="px-2 py-1">{v.notes}</td>
                <td className="px-2 py-1 text-right">
                  {i > 0 && (
                    <button
                      type="button"
                      className={btn}
                      onClick={() =>
                        confirm(`Publish version ${v.version} again as the newest?`) &&
                        rollback.mutate(v.version)
                      }
                    >
                      Roll back to this
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}
